import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Badge,
  useColorModeValue,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { Compass, MapPin, Scroll } from '@phosphor-icons/react';

export const AboutHeroSection = () => {
  // Use theme tokens directly
  const sectionBg = useColorModeValue('bg.surface', 'bg.surface'); // Placeholder, might need texture
  const badgeBg = useColorModeValue('bg.accent', 'bg.accent');
  const badgeColor = useColorModeValue('text.default', 'text.default'); // Check contrast
  const headingColor = useColorModeValue('interactive.primary', 'interactive.primary'); // Or text.default
  const textColorMuted = useColorModeValue('text.muted', 'text.muted');

  return (
    <Box
      bg={sectionBg}
      py={{ base: 20, md: 32 }}
      position="relative"
      overflow="hidden"
      minH={{ base: "70vh", md: "80vh" }}
      display="flex"
      alignItems="center"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: useColorModeValue(0.12, 0.18),
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-canvas)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '400px',
        bgBlendMode: useColorModeValue('soft-light', 'multiply'),
        zIndex: 0,
      }}
      _after={{
        content: '""',
        position: 'absolute',
        top: '20px',
        right: '20px',
        width: { base: '80px', md: '120px' },
        height: { base: '80px', md: '120px' },
        bgImage: 'var(--texture-compass)',
        bgRepeat: 'no-repeat',
        bgPosition: 'center',
        bgSize: 'contain',
        opacity: useColorModeValue(0.15, 0.25),
        pointerEvents: 'none',
        zIndex: 1,
        display: { base: 'none', md: 'block' },
      }}
    >
      <Container maxW="container.xl" position="relative" zIndex={2}>
        <VStack spacing={{ base: 8, md: 12 }} textAlign="center" alignItems="center">
          {/* Decorative Elements */}
          <Flex 
            align="center" 
            gap={4} 
            opacity={0.7}
            display={{ base: 'none', md: 'flex' }}
          >
            <Icon as={MapPin} boxSize={6} color={headingColor} />
            <Box w="60px" h="1px" bg={useColorModeValue('border.default', 'border.default')} />
            <Icon as={Compass} boxSize={8} color={headingColor} />
            <Box w="60px" h="1px" bg={useColorModeValue('border.default', 'border.default')} />
            <Icon as={Scroll} boxSize={6} color={headingColor} />
          </Flex>

          <Badge
            bg={badgeBg}
            color={badgeColor}
            fontSize={{ base: 'sm', md: 'md' }}
            px={{ base: 4, md: 6 }}
            py={{ base: 2, md: 3 }}
            borderRadius="md"
            fontWeight="medium"
            fontFamily="heading"
            letterSpacing="wide"
            border="1px solid"
            borderColor={useColorModeValue('border.light', 'border.light')}
            boxShadow="sm"
          >
            About Our Platform
          </Badge>

          <VStack spacing={6}>
            <Heading
              as="h1"
              fontFamily="accent"
              fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
              color={headingColor}
              lineHeight="1.1"
              letterSpacing="wide"
              textShadow="2px 2px 4px rgba(0,0,0,0.1)"
              position="relative"
              _after={{
                content: '""',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '2px',
                bg: headingColor,
                opacity: 0.6,
              }}
            >
              Megalith Maps
            </Heading>
            
            <Text
              fontFamily="body"
              fontSize={{ base: 'lg', md: 'xl' }}
              color={useColorModeValue('text.muted', 'text.muted')}
              fontWeight="medium"
              fontStyle="italic"
            >
              A comprehensive platform for megalithic site documentation and research
            </Text>
          </VStack>

          <Text
            fontFamily="body"
            fontSize={{ base: 'lg', md: 'xl' }}
            maxW="4xl"
            color={textColorMuted}
            lineHeight="tall"
            textAlign="center"
            px={{ base: 4, md: 0 }}
          >
            Megalith Maps is a community-driven platform dedicated to exploring and documenting megalithic sites around the world. 
            Our mission is to connect enthusiasts, researchers, and explorers, providing a collaborative space to share knowledge, 
            media, and discoveries about these ancient structures.
          </Text>

          {/* Mobile-friendly decorative element */}
          <Flex 
            align="center" 
            gap={3} 
            opacity={0.6}
            display={{ base: 'flex', md: 'none' }}
            mt={4}
          >
            <Icon as={MapPin} boxSize={5} color={headingColor} />
            <Box w="40px" h="1px" bg={useColorModeValue('border.default', 'border.default')} />
            <Icon as={Compass} boxSize={6} color={headingColor} />
            <Box w="40px" h="1px" bg={useColorModeValue('border.default', 'border.default')} />
            <Icon as={Scroll} boxSize={5} color={headingColor} />
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
};